import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import RankInsignia from "../../shared/rating/RankInsignia";
import { insigniaForValue } from "../../shared/rating/tiers";
import GamePreview from "../../shared/game-preview/GamePreview";
import ColorSwatchPicker from "../setup/ColorSwatchPicker";
import FlagPicker from "../setup/FlagPicker";
import Icon from "../setup/Icon";
import { playstylePresentation } from "../setup/presets";
import { previewConfig, PUBLIC_MAPS, PUBLIC_CAPS, COLOR_IDS, fmtClock } from "../lobby/lobby";
import { flagUrlSquare } from "../../shared/flags";

import lb from "../lobby/LobbyPage.module.scss";
import s from "./RankedLobbyLab.module.scss";

// ---------------------------------------------------------------------------
// /debug/ranked-lobby: how the start page could hold the two ranked modes (1v1
// and 6 FFA) next to the casual boxes, and what finding a match looks like.
//
// Decided before this was built: two separate ranked ratings, casual keeps its
// hidden-number tier "for fun", and pressing a ranked entry opens a MODAL that
// finds the match (never a visible room). While it searches you can play a
// local round, with the search pinned as a pill over the game, and a found
// match interrupts with an Accept overlay.
//
// Round 2 (this file's current focus): the casual boxes stay exactly as they
// are; the variants are blends of A (the strip) and B (the hero cards), plus
// the search flow as a four-step sequence. Round 1's C to F are kept for
// reference. Every layout renders from the same mock state, so the four states
// a ranked entry has to survive (signed out, unranked with placements left,
// ranked, searching) can be flipped on any of them. The phone frame passes
// mobile=true down explicitly; layouts key on that, not on the viewport.
// ---------------------------------------------------------------------------

// Same as LobbyPage's local helper: the playstyle chip wants its accent at 20%.
function hexToRgba(hex: string, a: number): string {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

type Mode = "duel" | "ffa";
type RankState = "signedOut" | "unranked" | "ranked" | "searching";
type VariantId = "O1" | "O2" | "O3" | "O4" | "O5" | "O6" | "O7" | "O8" | "O9" | "O10" | "O" | "P" | "Q" | "R" | "S" | "T" | "J" | "K" | "L" | "M" | "N" | "A" | "G" | "H" | "I" | "B" | "C" | "D" | "E" | "F";
type Flow = "none" | "modal" | "searching" | "playing" | "found";

const PLACEMENTS = 5;
// Mock ratings: a Gold II duelist who is a stronger Platinum I in crowds.
const MODE: Record<Mode, { title: string; short: string; blurb: string; rules: string; value: number; placed: number; worms: number; map: typeof PUBLIC_MAPS.small }> = {
  duel: { title: "1 vs 1", short: "1v1", blurb: "Best of 5 rounds", rules: "Best of 5 rounds · Classic power-ups · Small square", value: 1160, placed: 3, worms: 2, map: PUBLIC_MAPS.small },
  ffa: { title: "6 FFA", short: "6 FFA", blurb: "Six worms, last one standing", rules: "Six players · First to 30 points · Big square", value: 1340, placed: 0, worms: 6, map: PUBLIC_MAPS.medium },
};
const SEASON = "Season 1 · 23 days left";

const VARIANTS: { id: VariantId; name: string; round: 1 | 2 | 3 | 4 | 5; blurb: string }[] = [
  { id: "O1", name: "Eyebrow", round: 5, blurb: "O as picked, badge two pixels bigger. RANKED as the eyebrow over the mode name, number top-right, bar after the block. The control for the rest of this row." },
  { id: "O2", name: "Under the name", round: 5, blurb: "RANKED moves beneath the mode name as a sub-label, so the mode name is the first thing on the line." },
  { id: "O3", name: "In the block, below", round: 5, blurb: "RANKED lives inside the badge block, in tiny caps under the badge. The body is just the mode name, the number and the bar." },
  { id: "O4", name: "In the block, on edge", round: 5, blurb: "RANKED runs vertically up the left edge of the badge block, like a spine label. Nothing else in the body changes." },
  { id: "O5", name: "Top-right", round: 5, blurb: "RANKED sits top-right where the number was; the number moves to the end of the bar." },
  { id: "O6", name: "Tag beside the name (picked)", round: 5, blurb: "The pick. A small RANKED tag right after the mode name on the same line, number top-right. Placement matches fill the bar in a neutral off-white, not a tier colour and not the app cyan." },
  { id: "O7", name: "Above the bar", round: 5, blurb: "RANKED as a tiny right-aligned label sitting just above the bar's right end. Number stays top-right." },
  { id: "O8", name: "Section label", round: 5, blurb: "The word leaves the halves entirely: one RANKED section label with a rule runs above the whole strip. The halves hold only the badge, the mode, the number and the bar." },
  { id: "O9", name: "Prefix", round: 5, blurb: "RANKED as a smaller muted word on the same line before the mode name, no eyebrow, number top-right." },
  { id: "O10", name: "Trophy, no word", round: 5, blurb: "No word at all: a small trophy before the mode name says ranked. The quietest of the ten." },
  { id: "O", name: "Badge block, eyebrow", round: 4, blurb: "Two Lines with the badge as a full-height square on the left. The bar starts after it. RANKED is a tiny eyebrow above the mode name." },
  { id: "P", name: "Badge block, inline", round: 4, blurb: "Same block. The header reads RANKED · 1 VS 1 with RANKED muted and the mode bright, and the number sits at the end of the bar instead of the header." },
  { id: "Q", name: "Badge block, corner tag", round: 4, blurb: "Same block. RANKED is a small tag pinned in the top-right corner, the number under it, and the bar runs from the block to the edge." },
  { id: "R", name: "Tile, corners", round: 4, blurb: "Tiles with the centre cleared for the badge. 1 VS 1 top-left, RANKED top-right, the number bottom-right just above the bar, the bar along the bottom edge." },
  { id: "S", name: "Tile, eyebrow", round: 4, blurb: "The badge slides left of centre and bigger. RANKED over 1 VS 1 as a stacked label top-left, number bottom-right above the bar, bar as the bottom edge." },
  { id: "T", name: "Tile, footer", round: 4, blurb: "Badge centred and large. A footer line just above the bar holds 1 VS 1 on the left and the number on the right. RANKED is a corner tag top-right. The bar is inset, not an edge." },
  { id: "J", name: "Mode headline", round: 3, blurb: "H's height. The badge on the left, the mode name as the headline in Orbitron, the number small beneath it, and the live rating bar (same one as the match-end card, in the rank's colour) across the bottom. Ghost Play button, no gold anywhere." },
  { id: "K", name: "Bar beside the badge", round: 3, blurb: "The mode name is a header line across the half. Under it, the badge and then the bar filling the rest of the row, with the number at its end. The whole half is the button; the chevron says so." },
  { id: "L", name: "Card edge", round: 3, blurb: "Borrows the live boxes: the bar is the thin fill along the card's bottom edge, in the rank's colour, and a matching edge on the left. Badge big, mode name big, nothing else but the number and a ghost Play." },
  { id: "M", name: "Two lines", round: 3, blurb: "The most minimal. Line one: badge, mode name, number at the right. Line two: the bar. Nothing else. The half is the button." },
  { id: "N", name: "Tiles", round: 3, blurb: "Each mode as a centred tile: big badge on top, mode name under it, the bar under that. Two tiles fit side by side on a phone, so the phone layout stops stacking." },
  { id: "A", name: "Ranked strip", round: 2, blurb: "The strip as before: one half per mode with the insignia, tier and number, and a button. The whole half opens the ranked modal. Boxes untouched." },
  { id: "G", name: "Strip + progress", round: 2, blurb: "A's footprint with B's progress: a thin bar to the next sub-tier and the points left, under the rank. Still one row. The half is the button; the chevron just says so." },
  { id: "H", name: "Tall strip", round: 2, blurb: "B's card content in A's strip: a bigger insignia, the tier in Orbitron, the progress bar, your last five and the season tag, then a Play button. Taller than A, half the height of B." },
  { id: "I", name: "One bar", round: 2, blurb: "A single ranked bar. A trophy spine with the season on the left, then both modes as compact faces with a progress underline. The lightest of the round-2 set, and it leaves the most room for the boxes." },
  { id: "B", name: "Ranked hero", round: 1, blurb: "Round 1, for reference. Two big cards on top with insignia, number, progress and last five. Casual shrinks to quick-play tiles." },
  { id: "C", name: "One door", round: 1, blurb: "Round 1, for reference. A single RANKED card with both faces on it." },
  { id: "D", name: "Ranked takes the stack", round: 1, blurb: "Round 1, for reference. Duel and Brawl leave; the ranked modes take the stack as preview cards." },
  { id: "E", name: "Drop in", round: 1, blurb: "Round 1, for reference. Casual becomes one always-running arena." },
  { id: "F", name: "Rank on the player", round: 1, blurb: "Round 1, for reference. Both ranked insignia in the player row, queue buttons beneath." },
];

const STATES: { id: RankState; label: string }[] = [
  { id: "signedOut", label: "Signed out" },
  { id: "unranked", label: "Unranked" },
  { id: "ranked", label: "Ranked" },
  { id: "searching", label: "Searching" },
];

const FLOWS: { id: Flow; label: string; blurb: string }[] = [
  { id: "none", label: "Page", blurb: "" },
  { id: "modal", label: "1 · Modal", blurb: "Pressing a ranked entry opens this. Your rank, what the mode is, the season, and one button. On a phone it is a sheet from the bottom." },
  { id: "searching", label: "2 · Searching", blurb: "The same modal, now finding a match. A worm draws the ring. Cancel is always there, and the second button starts a local round to fill the wait." },
  { id: "playing", label: "3 · Play while you wait", blurb: "A local round with the search kept visible. Four treatments, switchable: the pill, the MS Battle radar card docked in a corner, a side rail, or a bottom bar. Cancel is always one tap away." },
  { id: "found", label: "4 · Match found", blurb: "No Accept. The moment a match is found the local round dims and the banner slams in, MS Battle's: your slab from the left, theirs from the right, a VS block between, then the countdown. The 6 FFA gets the starting grid instead: six seats glide in like a race start." },
];

// The one interactive thing: a ticking timer while a search is running.
function useClock(active: boolean, from = 12): number {
  const [t, setT] = useState(from);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setT((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
  return t;
}

// ---------- shared bits ----------

// A worm drawing a ring: the search spinner. The head sits at the leading end
// of the dash and rotates with it.
function WormRing({ size = 64 }: { size?: number }) {
  return (
    <svg className={s.ring} width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <circle className={s.ringTrack} cx="50" cy="50" r="40" />
      <circle className={s.ringWorm} cx="50" cy="50" r="40" />
      <circle className={s.ringHead} cx="24.8" cy="81.1" r="5.5" />
    </svg>
  );
}

// Insignia + text for one ranked mode, in whichever state. Only the duel ever
// "searches" in the mock, so the FFA face shows plain ranked in that state.
function RankFace({ mode, state, size = 38, gold = false }: { mode: Mode; state: RankState; size?: number; gold?: boolean }) {
  const m = MODE[mode];
  const modeCls = gold ? `${s.mode} ${s.modeGold}` : s.mode;
  if (state === "signedOut") {
    return (
      <span className={s.face}>
        <RankInsignia rating={null} placeholder placeholderTitle="Sign in to play ranked" size={size} />
        <span className={s.faceText}><span className={modeCls}>{m.title}</span><span className={s.dim}>Sign in to play ranked</span></span>
      </span>
    );
  }
  if (state === "unranked") {
    return (
      <span className={s.face}>
        <RankInsignia rating={null} placeholder placeholderTitle={`Placements: ${m.placed} of ${PLACEMENTS} played`} size={size} />
        <span className={s.faceText}>
          <span className={modeCls}>{m.title}</span>
          <span className={s.tierLabel}>Placements</span>
          <span className={s.pips} aria-label={`${m.placed} of ${PLACEMENTS} placement matches played`}>
            {Array.from({ length: PLACEMENTS }, (_, i) => <i key={i} className={i < m.placed ? `${s.pip} ${s.pipOn}` : s.pip} />)}
          </span>
        </span>
      </span>
    );
  }
  const info = insigniaForValue(m.value);
  return (
    <span className={s.face}>
      <RankInsignia value={m.value} size={size} />
      <span className={s.faceText}>
        <span className={modeCls}>{m.title}</span>
        <span className={s.tierLabel}>{info.label}</span>
        <span className={s.num}>{m.value}</span>
      </span>
    </span>
  );
}

// The entry label on a strip half. The half itself is the click target now
// (it opens the modal), so this is a styled span, not a nested button.
function EntryButton({ mode, state, label = "Play", className = "" }: { mode: Mode; state: RankState; label?: string; className?: string }) {
  const searching = state === "searching" && mode === "duel";
  const secs = useClock(searching);
  if (state === "signedOut") return <span className={`${s.queue} ${s.queueGhost} ${className}`}>Sign in</span>;
  if (searching) return <span className={`${s.queue} ${s.queueOn} ${className}`}>Searching {fmtClock(secs)} ✕</span>;
  if (state === "unranked") return <span className={`${s.queue} ${className}`}>Placement {MODE[mode].placed + 1}/{PLACEMENTS}</span>;
  return <span className={`${s.queue} ${className}`}>{label}</span>;
}

// Progress inside the current 100-point sub-tier band, and the label for it.
function progressOf(mode: Mode): { pct: number; left: number; next: string } {
  const v = MODE[mode].value;
  const pct = v % 100;
  const nextInfo = insigniaForValue(v - pct + 100);
  return { pct, left: 100 - pct, next: nextInfo.label };
}

function ProgressBar({ mode, thin = false }: { mode: Mode; thin?: boolean }) {
  const p = progressOf(mode);
  return (
    <span className={`${s.progWrap} ${thin ? s.progThin : ""}`}>
      <span className={s.prog}><i style={{ width: p.pct + "%" }} /></span>
      {!thin && <span className={s.progLbl}><span>{insigniaForValue(MODE[mode].value).label}</span><span>{p.left} to {p.next}</span></span>}
    </span>
  );
}

// Orbitron has no "›" glyph, so the entry hint is drawn.
function Chev() {
  return (
    <svg className={s.chev} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

function LastFive() {
  return <span className={s.recent} title="Last five"><i className={s.w} /><i className={s.w} /><i className={s.l} /><i className={s.w} /><i className={s.l} /></span>;
}

// The player row as it is today, with a slot for what sits where the badge goes.
function PlayerRow({ mobile, badge }: { mobile: boolean; badge: React.ReactNode }) {
  const [color, setColor] = useState(COLOR_IDS[1]);
  const [flag, setFlag] = useState("se");
  return (
    <div className={s.row}>
      <div className={s.card}>
        <ColorSwatchPicker colorId={color} colors={COLOR_IDS} onChange={setColor} />
        <span className={lb.lobbyRank}>{badge}</span>
        <span className={s.name}>RamenLord</span>
        <button className={s.chip}>{mobile ? "+" : "Add guest"}</button>
        {!mobile && <span className={s.keys}><span className={s.key}>LEFT</span><span className={s.key}>RIGHT</span></span>}
      </div>
      <FlagPicker code={flag} onChange={setFlag} />
    </div>
  );
}

// The casual insignia: the single hidden-number tier badge that the boxes use today.
function CasualBadge() {
  return <RankInsignia value={1220} size={36} />;
}

type BoxSize = "small" | "medium" | "large";
const BOX: Record<BoxSize, { title: string; blurb: string; playstyle: string; count: number; secs: number }> = {
  large: { title: "Royale Chaos", blurb: "Good luck", playstyle: "Sabotage", count: 19, secs: 61 },
  medium: { title: "Brawl", blurb: "Battle it out", playstyle: "Drink and drive", count: 2, secs: 103 },
  small: { title: "Duel", blurb: "Quick & tight", playstyle: "Classic", count: 1, secs: 82 },
};

// A casual box, borrowing the real card classes so it looks exactly like the
// live one, on mock counts. `forFun` adds the small tag that demotes its rank.
function CasualCard({ size, featured = false, forFun = false, className = "" }: { size: BoxSize; featured?: boolean; forFun?: boolean; className?: string }) {
  const b = BOX[size];
  const map = PUBLIC_MAPS[size];
  const cap = PUBLIC_CAPS[size];
  const pct = Math.round((b.count / cap) * 100);
  const ps = playstylePresentation(b.playstyle);
  return (
    <button className={`${lb.lobbyCard} ${featured ? lb.isFeatured : ""} ${className}`}>
      <div className={lb.lobbyPreview}><GamePreview matchConfig={previewConfig(map, cap, b.playstyle)} /></div>
      <div className={lb.lobbyShade} />
      <span className={`${lb.lobbyBadge} ${lb.lobbyBadgeTimer}`}>⏱ {fmtClock(b.secs)}</span>
      <span className={`${lb.lobbyBadge} ${lb.lobbyStyleChip} ${lb.lobbyStyleChipV9}`} style={{ "--chip-accent": ps.color, "--chip-accent-soft": hexToRgba(ps.color, 0.2) } as React.CSSProperties}>
        <Icon name={ps.icon} size={13} /><span>{b.playstyle}</span>
      </span>
      {forFun && <span className={s.forFun}>For fun · unranked</span>}
      <div className={lb.lobbyOverlay}>
        <div className={lb.lobbyCardInfo}>
          <div className={lb.lobbyCardTitle}>{b.title}</div>
          <div className={lb.lobbyCardSub}>{b.blurb} · {map.label}</div>
        </div>
        <span className={lb.lobbyPlayersN}>{b.count}<i>/{cap}</i> <span className={lb.lobbyPlayersLbl}>players</span></span>
      </div>
      <div className={lb.lobbyBar}><i style={{ width: pct + "%" }} /></div>
    </button>
  );
}

// Today's three boxes in today's arrangement (big left, two stacked right); the
// phone gets just the two small ones.
function CasualBoxes({ mobile, forFun }: { mobile: boolean; forFun: boolean }) {
  return (
    <div className={s.grid}>
      {!mobile && <CasualCard size="large" featured forFun={forFun} />}
      <div className={s.stack}>
        <CasualCard size="small" forFun={forFun} />
        <CasualCard size="medium" forFun={forFun} />
      </div>
    </div>
  );
}

function BottomButtons({ challenges = false }: { challenges?: boolean }) {
  return (
    <>
      <button className={`app-btn app-btn-primary ${s.local}`}>Local Play</button>
      <div className={s.actions}>
        <button className="app-btn app-btn-ghost">Host Game</button>
        <button className="app-btn app-btn-ghost">Join Game</button>
        {challenges && <button className={`app-btn app-btn-ghost ${s.soon}`} style={{ gridColumn: "1 / -1" }}>Challenges<small>Coming soon</small></button>}
      </div>
    </>
  );
}

// ---------- round 2: blends of A and B, boxes untouched ----------

function VariantA({ mobile, state }: { mobile: boolean; state: RankState }) {
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.strip}>
        {(["duel", "ffa"] as Mode[]).map((m) => (
          <button key={m} className={s.half}><RankFace mode={m} state={state} gold /><EntryButton mode={m} state={state} /></button>
        ))}
      </div>
      <CasualBoxes mobile={mobile} forFun={false} />
      <BottomButtons challenges />
    </div>
  );
}

function VariantG({ mobile, state }: { mobile: boolean; state: RankState }) {
  const ranked = state === "ranked" || state === "searching";
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.strip}>
        {(["duel", "ffa"] as Mode[]).map((m) => (
          <button key={m} className={`${s.half} ${s.halfG}`}>
            <RankFace mode={m} state={state} gold />
            {ranked && !mobile && <span className={s.halfProg}><ProgressBar mode={m} /></span>}
            {state === "searching" && m === "duel" ? <EntryButton mode={m} state={state} /> : <Chev />}
            {ranked && mobile && <ProgressBar mode={m} thin />}
          </button>
        ))}
      </div>
      <CasualBoxes mobile={mobile} forFun={false} />
      <BottomButtons />
    </div>
  );
}

function VariantH({ mobile, state }: { mobile: boolean; state: RankState }) {
  const ranked = state === "ranked" || state === "searching";
  const half = (m: Mode) => {
    const info = MODE[m];
    return (
      <button key={m} className={`${s.half} ${s.halfH}`}>
        <div className={s.hTop}>
          <span className={`${s.mode} ${s.modeGold}`}>Ranked · {info.title}</span>
          <span className={s.season}>Season 1</span>
        </div>
        <div className={s.hBody}>
          {ranked
            ? <RankInsignia value={info.value} size={56} />
            : <RankInsignia rating={null} placeholder size={56} placeholderTitle={state === "signedOut" ? "Sign in to play ranked" : "Placements"} />}
          <div className={s.hText}>
            {ranked ? <div className={s.heroTier}>{insigniaForValue(info.value).label}<span className={s.heroNum}>{info.value}</span></div>
              : state === "unranked" ? <div className={s.heroTier}>Placements <span className={s.heroNum}>{info.placed} of {PLACEMENTS}</span></div>
              : <div className={s.heroTier}>Unranked</div>}
            <div className={s.dim}>{info.blurb}</div>
            {ranked && <ProgressBar mode={m} />}
          </div>
          <div className={s.hRight}>
            {ranked && <LastFive />}
            <EntryButton mode={m} state={state} />
          </div>
        </div>
      </button>
    );
  };
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.strip}>{half("duel")}{half("ffa")}</div>
      <CasualBoxes mobile={mobile} forFun={false} />
      <BottomButtons />
    </div>
  );
}

function VariantI({ mobile, state }: { mobile: boolean; state: RankState }) {
  const ranked = state === "ranked" || state === "searching";
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.bar1}>
        <div className={s.barSpine}>
          <Icon name="trophy" size={18} />
          <span><span className={s.doorTitle} style={{ fontSize: 13 }}>Ranked</span><span className={s.season} style={{ display: "block" }}>{SEASON}</span></span>
        </div>
        {(["duel", "ffa"] as Mode[]).map((m) => (
          <button key={m} className={s.barFace}>
            <RankFace mode={m} state={state} size={32} />
            {state === "searching" && m === "duel" ? <EntryButton mode={m} state={state} /> : <Chev />}
            {ranked && <ProgressBar mode={m} thin />}
          </button>
        ))}
      </div>
      <CasualBoxes mobile={mobile} forFun={false} />
      <BottomButtons />
    </div>
  );
}

// ---------- round 1, kept for reference ----------

function VariantB({ mobile, state }: { mobile: boolean; state: RankState }) {
  const hero = (m: Mode) => {
    const info = MODE[m];
    const ranked = state === "ranked" || state === "searching";
    return (
      <div key={m} className={s.heroCard}>
        <div className={s.heroHead}><span className={`${s.mode} ${s.modeGold}`}>Ranked · {info.title}</span><span className={s.season}>Season 1</span></div>
        <div className={s.heroBody}>
          {ranked ? <RankInsignia value={info.value} size={64} /> : <RankInsignia rating={null} placeholder size={64} placeholderTitle={state === "signedOut" ? "Sign in to play ranked" : "Placements"} />}
          <div>
            {ranked ? <div className={s.heroTier}>{insigniaForValue(info.value).label}<span className={s.heroNum}>{info.value}</span></div>
              : state === "unranked" ? <div className={s.heroTier}>Placements <span className={s.heroNum}>{info.placed} of {PLACEMENTS}</span></div>
              : <div className={s.heroTier}>Unranked</div>}
            <div className={s.dim}>{info.blurb}</div>
          </div>
        </div>
        {ranked && <ProgressBar mode={m} />}
        <div className={s.heroFoot}>{ranked && <LastFive />}{ranked && <span className={s.dim}>~15s wait</span>}</div>
        <EntryButton mode={m} state={state} className={s.heroQueue} />
      </div>
    );
  };
  const tile = (size: BoxSize) => {
    const b = BOX[size];
    return (
      <button key={size} className={s.tile}>
        <div className={lb.lobbyPreview}><GamePreview matchConfig={previewConfig(PUBLIC_MAPS[size], PUBLIC_CAPS[size], b.playstyle)} /></div>
        <div className={lb.lobbyShade} />
        <span className={s.tileTimer}>⏱ {fmtClock(b.secs)}</span>
        <div className={s.tileTxt}><span className={s.tileName}>{b.title}</span><span className={s.tileN}>{b.count}/{PUBLIC_CAPS[size]}</span></div>
      </button>
    );
  };
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.hero}>{hero("duel")}{hero("ffa")}</div>
      <div className={s.sectionLbl}>Quick play · for fun</div>
      <div className={s.tiles}>{!mobile && tile("large")}{tile("medium")}{tile("small")}</div>
      <BottomButtons />
    </div>
  );
}

function VariantC({ mobile, state }: { mobile: boolean; state: RankState }) {
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.door}>
        <div className={s.doorSpine}><Icon name="trophy" size={22} /><span className={s.doorTitle}>Ranked</span></div>
        {(["duel", "ffa"] as Mode[]).map((m) => (
          <div key={m} className={s.doorFace}><RankFace mode={m} state={state} /><EntryButton mode={m} state={state} /></div>
        ))}
      </div>
      <CasualBoxes mobile={mobile} forFun={false} />
      <BottomButtons />
    </div>
  );
}

function VariantD({ mobile, state }: { mobile: boolean; state: RankState }) {
  const rankedCard = (m: Mode) => {
    const info = MODE[m];
    const ranked = state === "ranked" || state === "searching";
    return (
      <button key={m} className={`${lb.lobbyCard} ${s.rankedCard}`}>
        <div className={lb.lobbyPreview}><GamePreview matchConfig={previewConfig(info.map, info.worms, "Classic")} /></div>
        <div className={lb.lobbyShade} />
        <span className={s.badgeSlot}>
          {ranked ? <RankInsignia value={info.value} size={26} /> : <RankInsignia rating={null} placeholder size={26} />}
          <span className={s.tierLabel} style={{ fontSize: 12.5 }}>
            {ranked ? `${insigniaForValue(info.value).label} · ${info.value}` : state === "unranked" ? `Placements ${info.placed}/${PLACEMENTS}` : "Sign in"}
          </span>
        </span>
        <div className={lb.lobbyOverlay}>
          <div className={lb.lobbyCardInfo}>
            <div className={lb.lobbyCardTitle}>Ranked {info.short}</div>
            <div className={lb.lobbyCardSub}>{info.blurb}</div>
          </div>
        </div>
        <EntryButton mode={m} state={state} className={s.cardQueue} />
      </button>
    );
  };
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.grid}>
        {!mobile && <CasualCard size="large" featured forFun />}
        <div className={s.stack}>{rankedCard("duel")}{rankedCard("ffa")}{mobile && <CasualCard size="medium" forFun />}</div>
      </div>
      <BottomButtons />
    </div>
  );
}

function VariantE({ mobile, state }: { mobile: boolean; state: RankState }) {
  const size: BoxSize = mobile ? "medium" : "large";
  const map = PUBLIC_MAPS[size];
  const cap = PUBLIC_CAPS[size];
  const playing = mobile ? 7 : 23;
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={<CasualBadge />} />
      <div className={s.strip}>
        {(["duel", "ffa"] as Mode[]).map((m) => (
          <button key={m} className={s.half}><RankFace mode={m} state={state} size={32} gold /><EntryButton mode={m} state={state} /></button>
        ))}
      </div>
      <button className={`${lb.lobbyCard} ${s.arena}`}>
        <div className={lb.lobbyPreview}><GamePreview matchConfig={previewConfig(map, cap, "Sabotage")} /></div>
        <div className={lb.lobbyShade} />
        <span className={s.live}><i />{playing} playing now</span>
        <span className={s.roundIn}>Next round in 0:14</span>
        <div className={lb.lobbyOverlay} style={{ paddingRight: 108 }}>
          <div className={lb.lobbyCardInfo}>
            <div className={lb.lobbyCardTitle}>Drop in</div>
            <div className={lb.lobbyCardSub}>{mobile ? "Join at the next round" : `Always running · you join at the next round · ${map.label}`}</div>
          </div>
        </div>
        <span className={`${s.queue} ${s.dropCta}`}>Jump in</span>
      </button>
      <BottomButtons />
    </div>
  );
}

function VariantF({ mobile, state }: { mobile: boolean; state: RankState }) {
  const ranked = state === "ranked" || state === "searching";
  const badges = (
    <span className={s.rowBadges}>
      {(["duel", "ffa"] as Mode[]).map((m) =>
        ranked
          ? <RankInsignia key={m} value={MODE[m].value} size={36} />
          : <RankInsignia key={m} rating={null} placeholder size={36} placeholderTitle={state === "signedOut" ? "Sign in to play ranked" : `${MODE[m].title}: placements ${MODE[m].placed}/${PLACEMENTS}`} />,
      )}
    </span>
  );
  const btn = (m: Mode) => {
    const info = MODE[m];
    return (
      <button key={m} className={s.pairBtn}>
        {ranked ? <RankInsignia value={info.value} size={34} /> : <RankInsignia rating={null} placeholder size={34} />}
        <span><div className={s.pairLbl}>Queue {info.title}</div><div className={s.pairSub}>{info.blurb}</div></span>
        <span className={s.pairRight}>
          {ranked ? <><span className={s.tierLabel}>{insigniaForValue(info.value).label}</span><span className={s.num}>{info.value}</span></>
            : state === "unranked" ? <><span className={s.tierLabel}>Placements</span><span className={s.num}>{info.placed} of {PLACEMENTS}</span></>
            : <span className={s.dim}>Sign in</span>}
        </span>
      </button>
    );
  };
  return (
    <div className={s.col}>
      <PlayerRow mobile={mobile} badge={badges} />
      <div className={s.pair}>{btn("duel")}{btn("ffa")}</div>
      <CasualBoxes mobile={mobile} forFun />
      <BottomButtons />
    </div>
  );
}

// ---------- round 3: H's size, the badge and the mode name carry it ----------

// The rank's colour, or the egg's muted grey when there is no rank yet. Rides
// down as --tier exactly like the match-end card does.
function tierVar(mode: Mode, state: RankState): React.CSSProperties {
  const ranked = state === "ranked" || state === "searching";
  return { "--tier": ranked ? insigniaForValue(MODE[mode].value).tier.color : "#8995b3" } as React.CSSProperties;
}

function Badge3({ mode, state, size }: { mode: Mode; state: RankState; size: number }) {
  const ranked = state === "ranked" || state === "searching";
  return ranked
    ? <RankInsignia value={MODE[mode].value} size={size} />
    : <RankInsignia rating={null} placeholder size={size} placeholderTitle={state === "signedOut" ? "Sign in to play ranked" : `Placements: ${MODE[mode].placed} of ${PLACEMENTS}`} />;
}

// The live rating bar, copied from MatchEndModal: square, track at 8% white, a
// gradient from the tier colour to its 35% white tint, fill by transform.
// Unranked shows placements as five segments instead; signed out is empty.
function XpBar({ mode, state, height = 12, className = "" }: { mode: Mode; state: RankState; height?: number; className?: string }) {
  const ranked = state === "ranked" || state === "searching";
  if (state === "unranked") {
    return (
      <span className={`${s.xp} ${s.xpSeg} ${className}`} style={{ height }}>
        {Array.from({ length: PLACEMENTS }, (_, i) => <i key={i} className={i < MODE[mode].placed ? s.xpSegOn : ""} />)}
      </span>
    );
  }
  return <span className={`${s.xp} ${className}`} style={{ height }}><i style={{ transform: `scaleX(${ranked ? progressOf(mode).pct / 100 : 0})` }} /></span>;
}

function Num3({ mode, state }: { mode: Mode; state: RankState }) {
  const ranked = state === "ranked" || state === "searching";
  if (ranked) return <span className={s.num3}>{MODE[mode].value}</span>;
  return <span className={s.num3Dim}>{state === "signedOut" ? "Sign in" : `${MODE[mode].placed} / ${PLACEMENTS}`}</span>;
}

function Play3({ mode, state }: { mode: Mode; state: RankState }) {
  const searching = state === "searching" && mode === "duel";
  const secs = useClock(searching);
  if (searching) return <span className={`${s.queue} ${s.queueOn}`}>Searching {fmtClock(secs)} ✕</span>;
  return <span className={`app-btn app-btn-ghost ${s.play3}`}>{state === "signedOut" ? "Sign in" : "Play"}</span>;
}

function VariantJ({ mobile, state }: { mobile: boolean; state: RankState }) {
  const half = (m: Mode) => (
    <button key={m} className={`${s.h3} ${s.h3J}`} style={tierVar(m, state)}>
      <div className={s.h3Row}>
        <Badge3 mode={m} state={state} size={56} />
        <div className={s.h3Text}><span className={s.modeBig}>{MODE[m].title}</span><Num3 mode={m} state={state} /></div>
        <Play3 mode={m} state={state} />
      </div>
      <XpBar mode={m} state={state} />
    </button>
  );
  return (
    <div className={s.col}><PlayerRow mobile={mobile} badge={<CasualBadge />} /><div className={s.strip}>{half("duel")}{half("ffa")}</div><CasualBoxes mobile={mobile} forFun={false} /><BottomButtons /></div>
  );
}

function VariantK({ mobile, state }: { mobile: boolean; state: RankState }) {
  const half = (m: Mode) => (
    <button key={m} className={`${s.h3} ${s.h3K}`} style={tierVar(m, state)}>
      <div className={s.h3Head}><span className={s.modeBig}>{MODE[m].title}</span>{state === "searching" && m === "duel" ? <Play3 mode={m} state={state} /> : <Chev />}</div>
      <div className={s.h3Row}>
        <Badge3 mode={m} state={state} size={44} />
        <XpBar mode={m} state={state} className={s.xpGrow} />
        <Num3 mode={m} state={state} />
      </div>
    </button>
  );
  return (
    <div className={s.col}><PlayerRow mobile={mobile} badge={<CasualBadge />} /><div className={s.strip}>{half("duel")}{half("ffa")}</div><CasualBoxes mobile={mobile} forFun={false} /><BottomButtons /></div>
  );
}

function VariantL({ mobile, state }: { mobile: boolean; state: RankState }) {
  const half = (m: Mode) => (
    <button key={m} className={`${s.h3} ${s.h3L}`} style={tierVar(m, state)}>
      <div className={s.h3Row}>
        <Badge3 mode={m} state={state} size={60} />
        <div className={s.h3Text}><span className={s.modeBig}>{MODE[m].title}</span><Num3 mode={m} state={state} /></div>
        <Play3 mode={m} state={state} />
      </div>
      <XpBar mode={m} state={state} height={5} className={s.xpEdge} />
    </button>
  );
  return (
    <div className={s.col}><PlayerRow mobile={mobile} badge={<CasualBadge />} /><div className={s.strip}>{half("duel")}{half("ffa")}</div><CasualBoxes mobile={mobile} forFun={false} /><BottomButtons /></div>
  );
}

function VariantM({ mobile, state }: { mobile: boolean; state: RankState }) {
  const half = (m: Mode) => (
    <button key={m} className={`${s.h3} ${s.h3M}`} style={tierVar(m, state)}>
      <div className={s.h3Row}>
        <Badge3 mode={m} state={state} size={40} />
        <span className={s.modeBig}>{MODE[m].title}</span>
        <span className={s.h3Spacer} />
        {state === "searching" && m === "duel" ? <Play3 mode={m} state={state} /> : <Num3 mode={m} state={state} />}
      </div>
      <XpBar mode={m} state={state} />
    </button>
  );
  return (
    <div className={s.col}><PlayerRow mobile={mobile} badge={<CasualBadge />} /><div className={s.strip}>{half("duel")}{half("ffa")}</div><CasualBoxes mobile={mobile} forFun={false} /><BottomButtons /></div>
  );
}

function VariantN({ mobile, state }: { mobile: boolean; state: RankState }) {
  const tile = (m: Mode) => (
    <button key={m} className={`${s.h3} ${s.h3N}`} style={tierVar(m, state)}>
      <Badge3 mode={m} state={state} size={64} />
      <span className={s.modeBig}>{MODE[m].title}</span>
      <XpBar mode={m} state={state} height={10} className={s.xpFull} />
      {state === "searching" && m === "duel" ? <Play3 mode={m} state={state} /> : <Num3 mode={m} state={state} />}
    </button>
  );
  return (
    <div className={s.col}><PlayerRow mobile={mobile} badge={<CasualBadge />} /><div className={`${s.strip} ${s.stripTiles}`}>{tile("duel")}{tile("ffa")}</div><CasualBoxes mobile={mobile} forFun={false} /><BottomButtons /></div>
  );
}

// ---------- round 4: Two Lines and Tiles, pushed further ----------

// Two Lines with a full-height badge block: the shared shell for O, P, Q.
function BlockHalf({ mode, state, children }: { mode: Mode; state: RankState; children: React.ReactNode }) {
  return (
    <button className={`${s.h3} ${s.blk}`} style={tierVar(mode, state)}>
      <span className={s.blkBadge}><Badge3 mode={mode} state={state} size={52} /></span>
      <span className={s.blkBody}>{children}</span>
    </button>
  );
}
const strip4 = (mobile: boolean, half: (m: Mode) => React.ReactNode, tiles = false) => (
  <div className={s.col}><PlayerRow mobile={mobile} badge={<CasualBadge />} /><div className={`${s.strip} ${tiles ? s.stripTiles : ""}`}>{half("duel")}{half("ffa")}</div><CasualBoxes mobile={mobile} forFun={false} /><BottomButtons /></div>
);
const searchingOn = (m: Mode, state: RankState) => state === "searching" && m === "duel";

function VariantO({ mobile, state }: { mobile: boolean; state: RankState }) {
  return strip4(mobile, (m) => (
    <BlockHalf key={m} mode={m} state={state}>
      <span className={s.blkHead}>
        <span><span className={s.eyebrow}>Ranked</span><span className={s.modeBig}>{MODE[m].title}</span></span>
        {searchingOn(m, state) ? <Play3 mode={m} state={state} /> : <Num3 mode={m} state={state} />}
      </span>
      <XpBar mode={m} state={state} />
    </BlockHalf>
  ));
}

function VariantP({ mobile, state }: { mobile: boolean; state: RankState }) {
  return strip4(mobile, (m) => (
    <BlockHalf key={m} mode={m} state={state}>
      <span className={s.blkHead}>
        <span className={s.modeBig}><span className={s.inlineRanked}>Ranked · </span>{MODE[m].title}</span>
        {searchingOn(m, state) && <Play3 mode={m} state={state} />}
      </span>
      <span className={s.h3Row} style={{ gap: 10 }}><XpBar mode={m} state={state} className={s.xpGrow} /><Num3 mode={m} state={state} /></span>
    </BlockHalf>
  ));
}

function VariantQ({ mobile, state }: { mobile: boolean; state: RankState }) {
  return strip4(mobile, (m) => (
    <BlockHalf key={m} mode={m} state={state}>
      <span className={s.blkHead}>
        <span className={s.modeBig}>{MODE[m].title}</span>
        <span className={s.cornerCol}><span className={s.tag}>Ranked</span>{searchingOn(m, state) ? <Play3 mode={m} state={state} /> : <Num3 mode={m} state={state} />}</span>
      </span>
      <XpBar mode={m} state={state} />
    </BlockHalf>
  ));
}

function VariantR({ mobile, state }: { mobile: boolean; state: RankState }) {
  return strip4(mobile, (m) => (
    <button key={m} className={`${s.h3} ${s.tile4} ${s.tileR}`} style={tierVar(m, state)}>
      <span className={`${s.modeBig} ${s.tl}`}>{MODE[m].title}</span>
      <span className={`${s.eyebrow} ${s.tr}`}>Ranked</span>
      <span className={s.tileCentre}><Badge3 mode={m} state={state} size={60} /></span>
      <span className={s.br}>{searchingOn(m, state) ? <Play3 mode={m} state={state} /> : <Num3 mode={m} state={state} />}</span>
      <XpBar mode={m} state={state} height={5} className={s.xpEdge} />
    </button>
  ), true);
}

function VariantS({ mobile, state }: { mobile: boolean; state: RankState }) {
  return strip4(mobile, (m) => (
    <button key={m} className={`${s.h3} ${s.tile4} ${s.tileS}`} style={tierVar(m, state)}>
      <span className={s.tl}><span className={s.eyebrow}>Ranked</span><span className={s.modeBig}>{MODE[m].title}</span></span>
      <span className={s.tileLeftBadge}><Badge3 mode={m} state={state} size={72} /></span>
      <span className={s.br}>{searchingOn(m, state) ? <Play3 mode={m} state={state} /> : <Num3 mode={m} state={state} />}</span>
      <XpBar mode={m} state={state} height={5} className={s.xpEdge} />
    </button>
  ), true);
}

function VariantT({ mobile, state }: { mobile: boolean; state: RankState }) {
  return strip4(mobile, (m) => (
    <button key={m} className={`${s.h3} ${s.tile4} ${s.tileT}`} style={tierVar(m, state)}>
      <span className={`${s.tag} ${s.tr}`}>Ranked</span>
      <span className={s.tileCentre}><Badge3 mode={m} state={state} size={64} /></span>
      <span className={s.tileFoot}><span className={s.modeBig}>{MODE[m].title}</span>{searchingOn(m, state) ? <Play3 mode={m} state={state} /> : <Num3 mode={m} state={state} />}</span>
      <XpBar mode={m} state={state} height={10} className={s.xpFull} />
    </button>
  ), true);
}

// ---------- round 5: ten placements of the word, on O ----------

type Pos = "eyebrow" | "under" | "blockBelow" | "blockEdge" | "topRight" | "tag" | "aboveBar" | "section" | "prefix" | "trophy";
const POS: Record<string, Pos> = { O1: "eyebrow", O2: "under", O3: "blockBelow", O4: "blockEdge", O5: "topRight", O6: "tag", O7: "aboveBar", O8: "section", O9: "prefix", O10: "trophy" };

function O5Half({ mode, state, pos }: { mode: Mode; state: RankState; pos: Pos }) {
  const m = MODE[mode];
  const num = searchingOn(mode, state) ? <Play3 mode={mode} state={state} /> : <Num3 mode={mode} state={state} />;
  const word = <span className={s.eyebrow}>Ranked</span>;
  return (
    <button className={`${s.h3} ${s.blk} ${s.blk5}`} style={tierVar(mode, state)}>
      <span className={`${s.blkBadge} ${pos === "blockBelow" ? s.blkBadgeStack : ""} ${pos === "blockEdge" ? s.blkBadgeEdge : ""}`}>
        {pos === "blockEdge" && <span className={s.edgeWord}>Ranked</span>}
        <Badge3 mode={mode} state={state} size={54} />
        {pos === "blockBelow" && <span className={`${s.eyebrow} ${s.blockWord}`}>Ranked</span>}
      </span>
      <span className={s.blkBody}>
        <span className={s.blkHead}>
          <span className={s.nameCol}>
            {pos === "eyebrow" && word}
            <span className={s.modeBig}>
              {pos === "prefix" && <span className={s.inlineRanked}>Ranked </span>}
              {pos === "trophy" && <Icon name="trophy" size={15} />}
              {pos === "trophy" && " "}
              {m.title}
              {pos === "tag" && <span className={`${s.tag} ${s.tagInline}`}>Ranked</span>}
            </span>
            {pos === "under" && word}
          </span>
          {pos === "topRight" ? <span className={s.cornerCol}>{word}{searchingOn(mode, state) && <Play3 mode={mode} state={state} />}</span> : num}
        </span>
        {pos === "aboveBar" && <span className={`${s.eyebrow} ${s.aboveBar}`}>Ranked</span>}
        {pos === "topRight"
          ? <span className={s.h3Row} style={{ gap: 10 }}><XpBar mode={mode} state={state} className={s.xpGrow} /><Num3 mode={mode} state={state} /></span>
          : <XpBar mode={mode} state={state} />}
      </span>
    </button>
  );
}

function makeO(id: string): React.FC<{ mobile: boolean; state: RankState }> {
  const pos = POS[id];
  return function VariantO5({ mobile, state }) {
    return (
      <div className={s.col}>
        {/* No badge in the player row any more: the ranked strip is where rank lives. */}
        <PlayerRow mobile={mobile} badge={null} />
        {pos === "section" && <div className={s.sectionLbl}>Ranked</div>}
        <div className={s.strip}><O5Half mode="duel" state={state} pos={pos} /><O5Half mode="ffa" state={state} pos={pos} /></div>
        <CasualBoxes mobile={mobile} forFun={false} />
        <BottomButtons />
      </div>
    );
  };
}

const RENDER: Record<VariantId, React.FC<{ mobile: boolean; state: RankState }>> = { O1: makeO("O1"), O2: makeO("O2"), O3: makeO("O3"), O4: makeO("O4"), O5: makeO("O5"), O6: makeO("O6"), O7: makeO("O7"), O8: makeO("O8"), O9: makeO("O9"), O10: makeO("O10"), O: VariantO, P: VariantP, Q: VariantQ, R: VariantR, S: VariantS, T: VariantT, J: VariantJ, K: VariantK, L: VariantL, M: VariantM, N: VariantN, A: VariantA, G: VariantG, H: VariantH, I: VariantI, B: VariantB, C: VariantC, D: VariantD, E: VariantE, F: VariantF };

// ---------- the search flow: modal, searching, play while you wait, found ----------

// Steps 1 and 2. Centered on desktop, a bottom sheet on the phone. The mode is
// always the duel in the mock.
function RankModal({ searching, mobile }: { searching: boolean; mobile: boolean }) {
  const m = MODE.duel;
  const info = insigniaForValue(m.value);
  return (
    <div className={s.backdrop}>
      <div className={`${s.modal} ${mobile ? s.sheet : ""}`} role="dialog" aria-label="Ranked 1 vs 1">
        <div className={s.mHead}>
          <span className={s.mTitle}><Icon name="trophy" size={16} />Ranked · {m.title}</span>
          <button className={s.mClose} aria-label="Close">✕</button>
        </div>
        <div className={s.mSeason}>{SEASON}</div>

        <div className={s.mRank}>
          <RankInsignia value={m.value} size={72} />
          <div className={s.mRankText}>
            <div className={s.heroTier}>{info.label}<span className={s.heroNum}>{m.value}</span></div>
            <ProgressBar mode="duel" />
            <div className={s.mRow}><LastFive /><span className={s.dim}>3 of your last 5</span></div>
          </div>
        </div>

        <div className={s.mRules}>{m.rules}</div>

        {!searching ? (
          <>
            <button className={`${s.queue} ${s.mPrimary}`}>Find match</button>
            <div className={s.mHint}>Usually under 30 seconds</div>
          </>
        ) : (
          <>
            <FindingCard className={s.fcardInModal} />
            <button className={`${s.queue} ${s.mSecondary}`}>
              <span className={s.mSecTitle}>Play while you wait</span>
              <span className={s.mSecSub}>A local round. We pull you in the moment a match is found.</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- ported from MS Battle's MatchFound.tsx: the radar card, the slab banner, the grid ----------

type Wait = "dock" | "pill" | "radar" | "rail" | "bar";
const WAITS: { id: Wait; label: string }[] = [{ id: "dock", label: "Bar below map (picked)" }, { id: "pill", label: "Pill" }, { id: "radar", label: "Radar card" }, { id: "rail", label: "Side rail" }, { id: "bar", label: "Bar over map" }];

// The radar: a rotating sweep, two rings, a centre dot. found: a target blips.
function Radar({ size = 64, found = false }: { size?: number; found?: boolean }) {
  return (
    <span className={`${s.radar} ${found ? s.radarFound : ""}`} style={{ width: size, height: size }} aria-hidden="true">
      <i className={s.sweep} /><i className={s.ringA} /><i className={s.ringB} /><i className={s.rdot} /><i className={s.blip} />
    </span>
  );
}

// MS Battle's FindingEnemy card, in Achtung's clothes: radar, title with the
// blinking dots, a sub line, the clock in the accent. compact stacks it.
function FindingCard({ since = 12, compact = false, found = false, cancel = true, className = "" }: { since?: number; compact?: boolean; found?: boolean; cancel?: boolean; className?: string }) {
  const secs = useClock(!found, since);
  return (
    <div className={`${s.fcard} ${compact ? s.fcardCompact : ""} ${found ? s.fcardFound : ""} ${className}`} aria-live="polite">
      <Radar size={compact ? 52 : 64} found={found} />
      <div className={s.fstack}>
        <div className={`${s.ftext} ${s.ftextFinding}`}>
          <div className={s.ftitle}>Finding opponent<span className={s.fdots}><i /><i /><i /></span></div>
          <div className={s.fsub}>Searching the ladder for a worthy one</div>
        </div>
        <div className={`${s.ftext} ${s.ftextFound}`} aria-hidden={!found}>
          <div className={s.ftitle}>Opponent found</div>
          <div className={s.fsub}>Get ready</div>
        </div>
      </div>
      <div className={s.fclock}>{fmtClock(found ? 47 : secs)}</div>
      {cancel && !found && <button className={s.pillCancel}>Cancel</button>}
    </div>
  );
}

// Step 3: a local round underway, the search kept visible one of four ways.
function PlayingMock({ mobile, wait, foundMode }: { mobile: boolean; wait: Wait; foundMode: Mode | null }) {
  const found = foundMode != null;
  const secs = useClock(!found, 42);
  const w = mobile && wait === "rail" ? "bar" : wait;
  // The pick: a small map with the finding card as a real bar beneath it (above
  // it on the phone), nothing overlaid on the round itself.
  if (w === "dock") {
    return (
      <div className={`${s.playing} ${s.playingDock}`}>
        <div className={s.dock}>
          <div className={s.dockMap}><div className={s.playField}><GamePreview matchConfig={previewConfig(PUBLIC_MAPS.small, 4, "Classic")} /></div></div>
          <FindingCard found={found} className={s.dockBar} />
        </div>
        {foundMode === "duel" && <FoundBanner mobile={mobile} />}
        {foundMode === "ffa" && <FoundGrid mobile={mobile} />}
      </div>
    );
  }
  return (
    <div className={`${s.playing} ${w === "rail" ? s.playingRail : ""}`}>
      <div className={s.playField}><GamePreview matchConfig={previewConfig(PUBLIC_MAPS.small, 4, "Classic")} /></div>
      {w === "pill" && (
        <div className={s.pill}>
          <Radar size={22} found={found} />
          <span className={s.pillTxt}>Finding opponent<b>{fmtClock(found ? 47 : secs)}</b></span>
          <button className={s.pillCancel}>Cancel</button>
        </div>
      )}
      {w === "radar" && <FindingCard compact found={found} className={s.fcardCorner} />}
      {w === "bar" && <FindingCard found={found} className={s.fcardBar} />}
      {w === "rail" && (
        <aside className={s.rail}>
          <FindingCard compact found={found} cancel={false} />
          <div className={s.railRank}><span className={s.eyebrow}>Ranked · 1 vs 1</span><RankInsignia value={MODE.duel.value} size={44} /><span className={s.num3} style={{ color: insigniaForValue(MODE.duel.value).tier.color }}>{insigniaForValue(MODE.duel.value).label} · {MODE.duel.value}</span></div>
          <div className={s.railHint}>Your local round keeps going until a match is found</div>
          <button className={`app-btn app-btn-ghost ${s.railCancel}`}>Cancel search</button>
        </aside>
      )}
      {!mobile && w !== "rail" && w !== "bar" && !found && <div className={s.playHint}>Your local round keeps going until a match is found</div>}
      {foundMode === "duel" && <FoundBanner mobile={mobile} />}
      {foundMode === "ffa" && <FoundGrid mobile={mobile} />}
    </div>
  );
}

// Step 4, 1v1: MS Battle's banner. Your badge end and slab (your worm colour)
// from the left, theirs from the right, a black VS block between. No Accept:
// it holds a beat, splits, and the countdown takes over.
function FoundBanner({ mobile }: { mobile: boolean }) {
  return (
    <div className={`${s.fstage} ${mobile ? s.fstageStacked : ""}`} role="status" aria-label="RamenLord versus Kurvenkönig">
      <div className={s.fdim} />
      <div className={s.frow}>
        <div className={`${s.fgroup} ${s.fgroupL}`}>
          <div className={`${s.fend} ${s.pg}`}><RankInsignia value={MODE.duel.value} size={36} /></div>
          <div className={`${s.fslab} ${s.fslabL} ${s.pg}`}><span className={s.fname}>RamenLord</span><img className={s.fflag} src={flagUrlSquare("se")} alt="" /></div>
        </div>
        <div className={`${s.fmid} ${s.pg}`}>VS</div>
        <div className={`${s.fgroup} ${s.fgroupR}`}>
          <div className={`${s.fslab} ${s.fslabR} ${s.pg}`}><img className={s.fflag} src={flagUrlSquare("de")} alt="" /><span className={s.fname}>Kurvenkönig</span></div>
          <div className={`${s.fend} ${s.pg}`}><RankInsignia value={1105} size={36} /></div>
        </div>
      </div>
      <div className={s.fcount}>3</div>
    </div>
  );
}

// Step 4, 6 FFA: MS Battle's starting grid. Six seats, two by three, odd ones
// from the left and even ones from the right, each with the worm's colour on
// its edge, the flag, the name, the badge and the tier.
const GRID = [
  { name: "RamenLord", flag: "se", value: 1340, color: "#ff2e88", you: true },
  { name: "Kurvenkönig", flag: "de", value: 1305, color: "#28c4d4" },
  { name: "Zatacka_Zofia", flag: "pl", value: 1388, color: "#7cfc66" },
  { name: "TightTurner", flag: "gb", value: 1296, color: "#f5c542" },
  { name: "Slip_n_Slide", flag: "nl", value: 1412, color: "#b06cff" },
  { name: "InkTrail", flag: "cz", value: 1351, color: "#ff9f45" },
];
function FoundGrid({ mobile }: { mobile: boolean }) {
  return (
    <div className={`${s.fstage} ${mobile ? s.fstageCompact : ""}`} role="status" aria-label="Match found">
      <div className={s.fdim} />
      <div className={s.grid6}>
        {GRID.map((p, i) => (
          <div key={p.name} className={`${s.slot} ${p.you ? s.slotYou : ""}`} style={{ "--worm": p.color, "--from": i % 2 === 0 ? "-60%" : "60%" } as React.CSSProperties}>
            <img className={s.slotFlag} src={flagUrlSquare(p.flag)} alt="" />
            <span className={s.slotName}>{p.name}</span>
            <span className={s.slotRank} style={{ color: insigniaForValue(p.value).tier.color }}><RankInsignia value={p.value} size={mobile ? 18 : 22} /><span className={s.slotTier}>{insigniaForValue(p.value).label}</span></span>
          </div>
        ))}
      </div>
      <div className={s.fcount}>3</div>
    </div>
  );
}

// ---------- the lab ----------

export default function RankedLobbyLab() {
  const [params, setParams] = useSearchParams();
  const vParam = (params.get("v") || "O6").toUpperCase() as VariantId;
  const variant: VariantId = vParam in RENDER ? vParam : "O6";
  const state = (STATES.some((x) => x.id === params.get("state")) ? params.get("state") : "ranked") as RankState;
  const flow = (FLOWS.some((x) => x.id === params.get("flow")) ? params.get("flow") : "none") as Flow;
  const phone = params.get("phone") === "1";
  const wait = (WAITS.some((x) => x.id === params.get("wait")) ? params.get("wait") : "dock") as Wait;
  const foundMode = (params.get("found") === "ffa" ? "ffa" : "duel") as Mode;
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); n.set(k, v); setParams(n, { replace: true }); };
  const V = RENDER[variant];
  const meta = VARIANTS.find((x) => x.id === variant)!;
  const flowMeta = FLOWS.find((x) => x.id === flow)!;
  // From step 2 on, the page underneath is searching too.
  const effState: RankState = flow === "searching" || flow === "playing" || flow === "found" ? "searching" : state;

  return (
    <div className={s.lab}>
      <div className={s.bar}>
        <span className={s.barLabel}>Round 5</span>
        <div className={s.tabs}>
          {VARIANTS.filter((v) => v.round === 5).map((v) => (
            <button key={v.id} className={`${s.tab} ${v.id === variant ? s.tabOn : ""}`} onClick={() => set("v", v.id)}><b>{v.id}</b>{v.name}</button>
          ))}
        </div>
        <span className={s.barLabel}>Round 4</span>
        <div className={s.tabs}>
          {VARIANTS.filter((v) => v.round === 4).map((v) => (
            <button key={v.id} className={`${s.tab} ${s.tabDim} ${v.id === variant ? s.tabOn : ""}`} onClick={() => set("v", v.id)}><b>{v.id}</b>{v.name}</button>
          ))}
        </div>
        <span className={s.barLabel}>Round 3</span>
        <div className={s.tabs}>
          {VARIANTS.filter((v) => v.round === 3).map((v) => (
            <button key={v.id} className={`${s.tab} ${s.tabDim} ${v.id === variant ? s.tabOn : ""}`} onClick={() => set("v", v.id)}><b>{v.id}</b>{v.name}</button>
          ))}
        </div>
        <span className={s.barLabel}>Round 2</span>
        <div className={s.tabs}>
          {VARIANTS.filter((v) => v.round === 2).map((v) => (
            <button key={v.id} className={`${s.tab} ${s.tabDim} ${v.id === variant ? s.tabOn : ""}`} onClick={() => set("v", v.id)}><b>{v.id}</b>{v.name}</button>
          ))}
        </div>
        <span className={s.barLabel}>Round 1</span>
        <div className={s.tabs}>
          {VARIANTS.filter((v) => v.round === 1).map((v) => (
            <button key={v.id} className={`${s.tab} ${s.tabDim} ${v.id === variant ? s.tabOn : ""}`} onClick={() => set("v", v.id)}><b>{v.id}</b>{v.name}</button>
          ))}
        </div>
      </div>
      <div className={s.bar}>
        <span className={s.barLabel}>Finding a match</span>
        <div className={s.seg}>
          {FLOWS.map((x) => <button key={x.id} className={`${s.segBtn} ${x.id === flow ? s.segOn : ""}`} onClick={() => set("flow", x.id)}>{x.label}</button>)}
        </div>
        {(flow === "playing" || flow === "found") && (<>
          <span className={s.barLabel}>While you wait</span>
          <div className={s.seg}>
            {WAITS.map((x) => <button key={x.id} className={`${s.segBtn} ${x.id === wait ? s.segOn : ""}`} onClick={() => set("wait", x.id)}>{x.label}</button>)}
          </div>
        </>)}
        {flow === "found" && (<>
          <span className={s.barLabel}>Found</span>
          <div className={s.seg}>
            <button className={`${s.segBtn} ${foundMode === "duel" ? s.segOn : ""}`} onClick={() => set("found", "duel")}>1 vs 1 banner</button>
            <button className={`${s.segBtn} ${foundMode === "ffa" ? s.segOn : ""}`} onClick={() => set("found", "ffa")}>6 FFA grid</button>
          </div>
        </>)}
        <span className={s.barLabel}>State</span>
        <div className={s.seg}>
          {STATES.map((x) => <button key={x.id} className={`${s.segBtn} ${x.id === state ? s.segOn : ""}`} onClick={() => set("state", x.id)}>{x.label}</button>)}
        </div>
        <span className={s.barLabel}>Device</span>
        <div className={s.seg}>
          <button className={`${s.segBtn} ${!phone ? s.segOn : ""}`} onClick={() => set("phone", "0")}>Desktop</button>
          <button className={`${s.segBtn} ${phone ? s.segOn : ""}`} onClick={() => set("phone", "1")}>Phone</button>
        </div>
      </div>
      <p className={s.blurb}>
        <b>{variant}. {meta.name}.</b> {meta.blurb}
        {flow !== "none" && <><br /><b>{flowMeta.label}.</b> {flowMeta.blurb}</>}
      </p>
      <div className={`${phone ? s.phone : s.stage} ${s.stageRel}`}>
        {flow === "playing" || flow === "found"
          ? <PlayingMock mobile={phone} wait={wait} foundMode={flow === "found" ? foundMode : null} />
          : <V mobile={phone} state={effState} />}
        {(flow === "modal" || flow === "searching") && <RankModal searching={flow === "searching"} mobile={phone} />}
      </div>
    </div>
  );
}
